import { useEffect, useRef } from "react";

type Point = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  alpha: number;
};

export function MotionSystem() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const root = document.documentElement;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const finePointer = window.matchMedia("(pointer: fine)");
    let targetX = window.innerWidth * 0.5;
    let targetY = window.innerHeight * 0.35;
    let raf = 0;
    let ticking = false;

    const updateProgress = () => {
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      root.style.setProperty("--scroll-progress", String(window.scrollY / max));
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateProgress);
      }
    };
    const onPointerMove = (event: PointerEvent) => {
      targetX = event.clientX;
      targetY = event.clientY;
      root.style.setProperty("--pointer-x", `${event.clientX}px`);
      root.style.setProperty("--pointer-y", `${event.clientY}px`);
      root.style.setProperty("--pointer-nx", String(event.clientX / window.innerWidth - 0.5));
      root.style.setProperty("--pointer-ny", String(event.clientY / window.innerHeight - 0.5));
    };

    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -5%" });
    const cleanups: Array<() => void> = [];
    const boundTilt = new WeakSet<HTMLElement>();
    const bindTilt = (element: HTMLElement) => {
      if (boundTilt.has(element) || !finePointer.matches || reduceMotion.matches) return;
      boundTilt.add(element);
        const move = (event: PointerEvent) => {
          const rect = element.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width - 0.5;
          const y = (event.clientY - rect.top) / rect.height - 0.5;
          element.style.setProperty("--tilt-x", `${y * -8}deg`);
          element.style.setProperty("--tilt-y", `${x * 10}deg`);
          element.style.setProperty("--shine-x", `${(x + 0.5) * 100}%`);
          element.style.setProperty("--shine-y", `${(y + 0.5) * 100}%`);
        };
        const leave = () => {
          element.style.setProperty("--tilt-x", "0deg");
          element.style.setProperty("--tilt-y", "0deg");
        };
        element.addEventListener("pointermove", move);
        element.addEventListener("pointerleave", leave);
        cleanups.push(() => {
          element.removeEventListener("pointermove", move);
          element.removeEventListener("pointerleave", leave);
        });
    };
    const registerMotionElements = (rootNode: ParentNode) => {
      if (rootNode instanceof Element) {
        if (rootNode.matches("[data-reveal]")) revealObserver.observe(rootNode);
        if (rootNode.matches("[data-tilt]")) bindTilt(rootNode as HTMLElement);
      }
      rootNode.querySelectorAll("[data-reveal]").forEach((element) => revealObserver.observe(element));
      rootNode.querySelectorAll<HTMLElement>("[data-tilt]").forEach(bindTilt);
    };
    registerMotionElements(document);
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) registerMotionElements(node);
      }));
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    let points: Point[] = [];
    let width = 0;
    let height = 0;
    let dpr = 1;

    const resize = () => {
      if (!canvas || !context) return;
      width = window.innerWidth;
      height = window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = reduceMotion.matches ? 20 : Math.min(74, Math.max(32, Math.round(width / 20)));
      points = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.18,
        radius: Math.random() * 1.4 + 0.4,
        alpha: Math.random() * 0.45 + 0.18,
      }));
    };

    const draw = () => {
      if (!context) return;
      context.clearRect(0, 0, width, height);
      for (let i = 0; i < points.length; i += 1) {
        const point = points[i];
        if (!reduceMotion.matches) {
          point.x += point.vx;
          point.y += point.vy;
          if (point.x < -20) point.x = width + 20;
          if (point.x > width + 20) point.x = -20;
          if (point.y < -20) point.y = height + 20;
          if (point.y > height + 20) point.y = -20;
          const dx = point.x - targetX;
          const dy = point.y - targetY;
          const distance = Math.hypot(dx, dy);
          if (distance < 160 && distance > 0) {
            point.x += (dx / distance) * 0.18;
            point.y += (dy / distance) * 0.18;
          }
        }
        context.beginPath();
        context.fillStyle = `rgba(211, 255, 104, ${point.alpha})`;
        context.arc(point.x, point.y, point.radius, 0, Math.PI * 2);
        context.fill();
        for (let j = i + 1; j < points.length; j += 1) {
          const other = points[j];
          const distance = Math.hypot(point.x - other.x, point.y - other.y);
          if (distance < 118) {
            context.beginPath();
            context.strokeStyle = `rgba(141, 120, 255, ${(1 - distance / 118) * 0.08})`;
            context.lineWidth = 0.7;
            context.moveTo(point.x, point.y);
            context.lineTo(other.x, other.y);
            context.stroke();
          }
        }
      }
      if (!reduceMotion.matches) raf = requestAnimationFrame(draw);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", resize, { passive: true });
    if (finePointer.matches) window.addEventListener("pointermove", onPointerMove, { passive: true });
    resize();
    updateProgress();
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onPointerMove);
      revealObserver.disconnect();
      mutationObserver.disconnect();
      cleanups.forEach((cleanup) => cleanup());
    };
  }, []);

  return <>
    <div className="scroll-progress" aria-hidden="true" />
    <div className="cursor-aura" aria-hidden="true" />
    <canvas className="particle-field" ref={canvasRef} aria-hidden="true" />
  </>;
}
